import {
  type ApplicantFlowInsightsRequestInput,
  type ApplicantFlowInsightsRequestOutput,
  CustomerSuccessPersistence,
  type GoldUpgradeRequestOutput,
  type LaborMarketReportRequestInput,
  type LaborMarketReportRequestOutput,
  type LaborMarketReportType,
  LocationScope,
  ReportType,
} from './customer-success.persistence';
import MeService from '../shared/me/me.service';

export class CustomerSuccessService {
  private persistence = new CustomerSuccessPersistence();
  private meService = new MeService();

  /**
   * Submits a request for a labor market report
   * @param jobTitle The job title to search for
   * @param location The location to search in
   * @param distance The distance in miles from the location
   * @param reportType The type of report (STANDARD or RELOCATION)
   * @param locationScope The scope of the location search (metro, state, or nation)
   * @returns The response from the API
   */
  public async submitLaborMarketReportRequest(
    jobTitle: string,
    location: string | undefined,
    distance: string | number | undefined,
    reportType: LaborMarketReportType = ReportType.STANDARD,
    locationScope: LocationScope = LocationScope.METRO,
  ): Promise<LaborMarketReportRequestOutput | null> {
    const isStandard = reportType === 'STANDARD';

    let resolvedLocation = location || '';
    let searchRadius: number | undefined;

    if (locationScope === LocationScope.NATION) {
      resolvedLocation = 'United States (Nationwide)';
      searchRadius = 100;
    } else if (locationScope === LocationScope.STATE) {
      resolvedLocation = location || '';
      searchRadius = 0;
    } else {
      // LocationScope.METRO
      resolvedLocation = location || '';
      searchRadius = isStandard ? (typeof distance === 'number' ? distance : 30) : undefined;
    }

    const request: LaborMarketReportRequestInput = {
      jobTitle,
      location: resolvedLocation,
      reportType,
      ...(searchRadius !== undefined ? { searchRadius } : {}),
    };

    return this.persistence.submitLaborMarketReport(request);
  }

  /**
   * Submits a request for applicant flow insights
   */
  public async submitApplicantFlowInsightsRequest(
    jobId: number,
    jobTitle: string,
    location: string | undefined,
  ): Promise<ApplicantFlowInsightsRequestOutput | null> {
    const request: ApplicantFlowInsightsRequestInput = {
      jobId,
      jobTitle,
      location: location || '',
    };

    return this.persistence.submitApplicantFlowInsights(request);
  }

  /**
   * Submits a request for gold upgrade
   */
  public async submitGoldUpgradeRequest(): Promise<GoldUpgradeRequestOutput | null> {
    return this.persistence.submitGoldUpgradeRequest();
  }

  /**
   * Submits a Job Match Report request for a batch of selected candidates and/or
   * uploaded resumes. Each candidate and each resume is one backend call; all calls
   * in a single submission share one batchId. Returns success/failure counts plus the
   * batchId, which is surfaced to the recruiter as the reference ID for the whole batch.
   */
  public async submitJobMatchReport(params: {
    candidates: { userProfileId: number }[];
    resumes: { resumeBase64: string; resumeFileName?: string }[];
  }): Promise<{ successCount: number; failureCount: number; batchId: string }> {
    const recruiterEmail = this.meService.userProfile?.email;
    const recruiterFirstName = this.meService.userProfile?.nameFirst;

    if (!recruiterEmail || !recruiterFirstName) {
      throw new Error('Cannot submit Job Match Report: missing recruiter email or first name.');
    }

    const batchId = crypto.randomUUID();

    const calls: Promise<{ success: boolean } | null>[] = [
      ...params.candidates.map((candidate) =>
        this.persistence.submitMarketableCandidateReportFromUser({
          batchId,
          recruiterEmail,
          recruiterFirstName,
          candidateUserProfileId: candidate.userProfileId,
        }),
      ),
      ...params.resumes.map((resume) =>
        this.persistence.submitMarketableCandidateReportWithResume({
          batchId,
          recruiterEmail,
          recruiterFirstName,
          resumeBase64: resume.resumeBase64,
          resumeFileName: resume.resumeFileName,
        }),
      ),
    ];

    const results = await Promise.allSettled(calls);

    let successCount = 0;
    let failureCount = 0;
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value?.success) {
        successCount += 1;
      } else {
        failureCount += 1;
      }
    }

    return { successCount, failureCount, batchId };
  }
}
